// hoursPicker — hours are a TIME. One unit, on every screen that touches pay.
//
// ── The report ─────────────────────────────────────────────────────────────
//
// Owner, 2026-09-16, looking at an approved sheet:
//
//   "In the screenshot shows the worker adjusted the hours to 2.83??? 83 min??
//    wtf?? Also, it says clock said 2.75 how does that make sense? Also, it
//    shows there was an adjustment, but the week says 2:49=56.60. the math
//    doesn't add up but it seems like the math is picking up 2.83x20=56.60"
//
// Every one of those reads correctly, and the cause is the same for all three:
// ONE SCREEN WAS SPEAKING TWO UNITS.
//
// Reproduced from the real functions (scripts are throwaway; the numbers are
// pinned in hoursPicker.test.js), $20/h:
//
//   the edit box        `<input type=number step=0.25>`   DECIMAL HOURS
//   its hint            "Clock said 2.75"                 DECIMAL HOURS
//   the grid            2:49                              H:MM
//   the week            7:19                              H:MM
//   the change history  2:45 → 2:49                       H:MM
//   the pay             $56.60                            2.83 x 20
//
// So:
//
//   · "2.83" is 2 h 49.8 m. A person typing it into a box on a page covered in
//     H:MM almost certainly means 2 h 83 m — which is not a time at all. The
//     form asked for the one unit nothing else on the page used.
//
//   · "Clock said 2.75" IS "2:45". Same number, wrong unit. (And it is
//     already rounded: hoursModel.clockLinesForWeek stores
//     `roundQuarter(seconds / 3600)`, so it is not the raw clock either.)
//
//   · The money is arithmetically RIGHT and impossible to check by eye.
//     $56.60 is exactly 2.83 x 20, which is exactly what was stored. But the
//     grid shows 2:49, because H:MM throws away the last 0.8 of a minute. Read
//     2:49 off the screen and you compute $56.33. Twenty-seven cents that the
//     screen cannot explain — on the page where somebody approves payroll.
//     CLAUDE.md calls a plausible-looking wrong number the worst outcome
//     available here, and a number you cannot reconcile is one of those.
//
// ── The cure: a time, picked, never typed ──────────────────────────────────
//
// Hours and minutes are chosen from lists. There is no box a decimal can be
// typed into, so "2.83" cannot be entered and cannot be meant. The value is
// always a whole number of minutes, which makes the H:MM everywhere else on
// the page EXACT — 2:50 stored is 2:50 shown is 2:50 paid — so a person can
// reconcile the row by reading it.
//
// ── QUARTER hours, and the first cut of this got it wrong ─────────────────
//
// It offered FIVE-minute steps, reasoning that quarters would drag existing
// off-grid rows further (2.83 -> 2:45, a $1.60 move at $20/h, against $0.07
// for 2:50). That reasoning was about the wrong cost, and review caught it.
//
// Hours reach a paystub through `totalHours.toFixed(2)`, while gross_pay is
// computed from the UNROUNDED value (routes/timesheets.js:371, :393,
// routes/paystubs.js:416). So a stored figure that is not exactly two decimal
// places puts a paystub out by cents — the same defect this file was written
// to kill, moved from the timesheet row to the document the worker is handed.
// Measured through the real computeFigures:
//
//   5-minute steps    8 of 12 choices do not reconcile. 2:05 stores 2.0833…,
//                     prints "2.08 h" and "$41.67", and 2.08 x 20 is $41.60.
//   3-minute steps    0 of 20 — every multiple of 3 minutes is exact at 2dp
//   quarter hours     0 of 4
//
// Both of the clean options work. Quarters win on the argument that matters:
// THE CLOCK IS ALREADY QUARTER-ROUNDED AT INGEST (hoursModel.clockLinesForWeek
// stores roundQuarter(seconds / 3600)). A manual path offering finer
// granularity than the automatic one is offering precision the system does not
// have anywhere else — and that mismatch is exactly what produced 2.83 in the
// first place. The correction now lands on the same grid as the record it is
// correcting, so typed hours and clock hours are directly comparable and
// `sameHours` is exact on both sides.
//
// The cost is real and stays visible: a line already off the grid moves
// further when it is opened and saved, and offGridNotice says so by name
// before anything is written.
//
// If finer is ever wanted, 3-minute steps is the answer — 20 options, all
// exact at 2dp, all lossless in H:MM — and not 5 or 10, which are neither.
//
// ── What this module does NOT do ───────────────────────────────────────────
//
// It computes no money. Not a preview, not a subtotal, nothing. Every figure
// on these screens is a return value of server/utils/paystubCalc.js, and
// working one out on a screen is #202's exact shape. This module converts
// between a decimal the database stores and the two numbers a person picks.

/**
 * The minutes list, and the grid every stored value is snapped to.
 *
 * 15 because a quarter hour is the one unit that is exact in BOTH grids this
 * application prints hours in: a whole number of minutes (so H:MM is
 * lossless) and an exact two-decimal figure (so the paystub reconciles). The
 * header says why it is not 5.
 */
export const MINUTE_STEP = 15;

/**
 * 0..16. Sixteen hours is past any legal shift and well past any real one; the
 * list has to end somewhere and a `<select>` that has to be scrolled for a
 * plausible value is worse than one that cannot express an implausible one.
 * A line already storing more than this keeps it — see hoursToParts.
 */
export const MAX_HOURS = 16;

export const HOUR_CHOICES = Array.from({ length: MAX_HOURS + 1 }, (_, i) => i);
export const MINUTE_CHOICES = Array.from({ length: 60 / MINUTE_STEP }, (_, i) => i * MINUTE_STEP);

/** Whole minutes in a decimal-hours value, to the nearest minute. */
export function minutesOf(decimalHours) {
  const n = Number(decimalHours);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 60);
}

/**
 * Decimal hours → the { hours, minutes } a person picks.
 *
 * Snapped to MINUTE_STEP, because the picker cannot express anything else and
 * an editor that opens on a value its own controls cannot hold is an editor
 * that changes the number just by being opened without saying so.
 *
 * The hours part is NOT capped. A line storing 20 hours is wrong, but hiding
 * that by opening the editor on 16 would be a silent edit of somebody's pay;
 * the picker adds the out-of-range value to its own list instead
 * (hourChoicesFor below), so it is visible and can be corrected.
 */
export function hoursToParts(decimalHours) {
  const stepped = Math.round(minutesOf(decimalHours) / MINUTE_STEP) * MINUTE_STEP;
  return { hours: Math.floor(stepped / 60), minutes: stepped % 60 };
}

/** { hours, minutes } → decimal hours, exact to the minute. */
export function partsToHours(hours, minutes) {
  const h = Number(hours);
  const m = Number(minutes);
  const total = (Number.isFinite(h) && h > 0 ? Math.floor(h) : 0) * 60
    + (Number.isFinite(m) && m > 0 ? Math.round(m) : 0);
  return total / 60;
}

/**
 * The hours list this line needs: the normal one, plus its own value when that
 * is off the end. See hoursToParts for why it is not simply clamped.
 */
export function hourChoicesFor(decimalHours) {
  const { hours } = hoursToParts(decimalHours);
  if (hours <= MAX_HOURS) return HOUR_CHOICES;
  return [...HOUR_CHOICES, hours];
}

/**
 * Decimal hours → 'H:MM'. The SAME reading as dispatch/lib/format.js
 * `hoursClock`, which is what the grid, the totals and the change history use
 * — restated here so a form can label its own controls without importing an
 * application's private helper, and pinned equal to it by hoursPicker.test.js.
 */
export function clockLabel(decimalHours) {
  const total = minutesOf(decimalHours);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * Is this value one the picker can hold exactly?
 *
 * False for every line stored before this existed that is not a whole quarter
 * hour — the 2.83 in the report is 169.8 minutes. The form says so out
 * loud rather than quietly moving it, because on this screen a number moving
 * by itself is the whole complaint.
 */
export function isOnGrid(decimalHours) {
  const n = Number(decimalHours);
  if (!Number.isFinite(n) || n < 0) return false;
  const exactMinutes = n * 60;
  if (Math.abs(exactMinutes - Math.round(exactMinutes)) > 1e-6) return false;
  return Math.round(exactMinutes) % MINUTE_STEP === 0;
}

/**
 * The nearest value the picker CAN hold. **A PERSON may ask for it. A save
 * path must not apply it.**
 *
 * That distinction is the whole of this function's story. Its one caller in
 * the app is the "Set to 2:45" button inside HoursField's off-grid notice —
 * a control somebody presses, that says what it will do before they press it.
 *
 * The history, because the sentence above reads like an oversight and is not.
 * The off-grid notice used to promise "saving will set it to 2:45" while every
 * save path sent the raw stored figure — nothing calls onChange on mount, so a
 * person who pressed Save without touching a select wrote 2.83 straight back
 * and the sentence they had just read was false. The fix for THAT was to snap
 * at save, and it cost more than it bought: a line stored 2.83 against a clock
 * of 2.75 snapped exactly ONTO the clock, so no reason was demanded, the
 * server recorded it as matching the clock, and the Adjusted mark and its
 * reason came off a payroll row while the pay fell $1.60 — all from opening an
 * editor and pressing a button. Reverted in 6df04ff.
 *
 * So the rule is: a stored payroll figure is never moved because somebody
 * LOOKED at it — only because they ASKED. Putting `snapToGrid(` back into
 * either save() reintroduces the defect, and SheetDetail.test.js:1582 is the
 * test that goes red when somebody does.
 */
export function snapToGrid(decimalHours) {
  const { hours, minutes } = hoursToParts(decimalHours);
  return partsToHours(hours, minutes);
}

/**
 * The sentence a form shows when opening on a value its controls cannot hold,
 * or null.
 *
 * ── It used to promise a move. Now it explains one that will not happen ────
 *
 * The first version read "Saving will set it to 2:45", and the save path did
 * not do that — a person who pressed Save without touching a control wrote
 * the original figure straight back, so the sentence was false. The fix for
 * THAT was to snap at save, and review found what it cost: a line stored at
 * 2.83 against a clock of 2.75 snapped exactly ONTO the clock, so no reason
 * was demanded, the server recorded it as matching the clock, and the
 * Adjusted mark and its reason came off the line while the pay fell $1.60.
 * All from opening an editor and pressing Save.
 *
 * So nothing snaps unless a person picks something. A stored payroll figure is
 * never moved as a side effect of being LOOKED at, which is the rule that
 * should have been obvious first. This sentence says what the line is and what
 * the controls can do, and promises nothing.
 */
export function offGridNotice(decimalHours) {
  if (isOnGrid(decimalHours)) return null;
  const n = Number(decimalHours);
  if (!Number.isFinite(n) || n < 0) return null;
  const { hours, minutes } = hoursToParts(n);
  const exact = n * 60;
  return `This line is ${clockLabel(n)} — ${(Math.round(exact * 10) / 10)} minutes, which is not a `
    + `whole quarter hour. It stays as it is unless you change it; the controls above set quarter `
    + `hours, and the nearest is ${hours}:${String(minutes).padStart(2, '0')}.`;
}


/**
 * Are two hours figures the same TIME, to the minute?
 *
 * The MIRROR of `sameHours` in server/utils/hoursModel.js, which decides
 * whether a line is recorded as matching the clock or as ADJUSTED. The server
 * owns that decision; this copy exists so a form can ask for a reason at the
 * moment somebody changes the number, instead of letting them save and then
 * discovering the server marked it. hoursPicker.test.js drives both readings
 * over a grid and requires them to agree.
 *
 * Both used to round to the quarter hour, which made a five-minute edit read
 * as "matches the clock" — see the note on the server copy for the
 * measurement, and for why the quarter was never a tolerance anybody chose.
 */
export function sameHours(a, b) {
  return Math.round((Number(a) || 0) * 60) === Math.round((Number(b) || 0) * 60);
}
